/**
 * Residual grounding — the nonlinear counterpart of `interpret.ts`'s linear
 * lowering (see docs/nonlinear-backends.md).
 *
 * The linear path folds every expression into a coefficient map and throws on a
 * product of two variables. This path keeps those products: it lowers an
 * expression into an {@link RNode} expression graph over the decision variables,
 * which a solver can evaluate and differentiate at a point. Everything *around*
 * the lowering is shared with the linear path — the conjunctive-query grounding
 * of the quantifiers, index/scalar evaluation, data lookups, and variable naming
 * all come from `interpret.ts`'s {@link makeGrounder}, called verbatim. That is
 * the whole point: the front half of the language is backend-agnostic, and only
 * the lowering target changes.
 *
 * A comparison rule (`left == right`) grounds into one residual `left - right`
 * per binding, which the solver drives to zero.
 */

import {
  Catalog,
  Data,
  Rule,
  Expr,
  Env,
  Grounder,
  makeGrounder,
  instanceName,
  formatIndexValue,
} from '../language';
import { IndexValue } from '../highs';

// ---------------------------------------------------------------------------
// The residual IR
// ---------------------------------------------------------------------------

/** A node of a residual expression graph over the decision variables. */
export type RNode =
  | { k: 'const'; v: number }
  | { k: 'var'; name: string }
  | { k: 'neg'; x: RNode }
  | { k: 'bin'; op: '+' | '-' | '*' | '/'; l: RNode; r: RNode }
  | { k: 'fn'; fn: 'sqrt' | 'sin' | 'cos'; x: RNode };

const konst = (v: number): RNode => ({ k: 'const', v });
const rvar = (name: string): RNode => ({ k: 'var', name });
const rneg = (x: RNode): RNode => ({ k: 'neg', x });
const rbin = (op: '+' | '-' | '*' | '/', l: RNode, r: RNode): RNode => ({ k: 'bin', op, l, r });
const rfn = (fn: 'sqrt' | 'sin' | 'cos', x: RNode): RNode => ({ k: 'fn', fn, x });

/** A grounded residual: its instance name, the rule it came from, and the graph. */
export interface GroundedResidual {
  /** e.g. `p_balance_bus2_t0`. */
  name: string;
  /** The rule name the residual grounded from, e.g. `p_balance`. */
  group: string;
  /** Index column values from the rule's quantifiers, e.g. `{ bus: 'bus2', timestep: 0 }`. */
  indices: Record<string, IndexValue>;
  /** The residual expression; a solution drives this to 0. */
  node: RNode;
}

/**
 * Grouping metadata for a grounded decision variable — the relation it belongs
 * to and the values of that relation's index columns. The same `group`/`indices`
 * the LP grounder attaches to its variables, so an AC solution flows into the
 * shared solution-table extractors unchanged.
 */
export interface VarMeta {
  group: string;
  indices: Record<string, IndexValue>;
}

// ---------------------------------------------------------------------------
// Forward-mode autodiff
// ---------------------------------------------------------------------------

/** A value paired with its gradient w.r.t. every variable it depends on. */
export interface Jet {
  value: number;
  grad: Map<string, number>;
}

const axpy = (into: Map<string, number>, s: number, g: ReadonlyMap<string, number>): void => {
  for (const [k, v] of g) into.set(k, (into.get(k) ?? 0) + s * v);
};

/**
 * Evaluate a residual node at a point `x`, returning its value and full sparse
 * gradient in one forward pass. This is the row of the Jacobian for that
 * residual — the gradient's keys are exactly the variables the residual touches,
 * which is the Jacobian's sparsity pattern, for free.
 */
export function evalJet(n: RNode, x: ReadonlyMap<string, number>): Jet {
  switch (n.k) {
    case 'const':
      return { value: n.v, grad: new Map() };
    case 'var':
      return { value: x.get(n.name) ?? 0, grad: new Map([[n.name, 1]]) };
    case 'neg': {
      const a = evalJet(n.x, x);
      const grad = new Map<string, number>();
      axpy(grad, -1, a.grad);
      return { value: -a.value, grad };
    }
    case 'bin': {
      const a = evalJet(n.l, x);
      const b = evalJet(n.r, x);
      const grad = new Map<string, number>();
      switch (n.op) {
        case '+':
          axpy(grad, 1, a.grad);
          axpy(grad, 1, b.grad);
          return { value: a.value + b.value, grad };
        case '-':
          axpy(grad, 1, a.grad);
          axpy(grad, -1, b.grad);
          return { value: a.value - b.value, grad };
        case '*':
          // (uv)' = u'v + uv'
          axpy(grad, b.value, a.grad);
          axpy(grad, a.value, b.grad);
          return { value: a.value * b.value, grad };
        case '/': {
          // (u/v)' = u'/v - u v'/v^2
          axpy(grad, 1 / b.value, a.grad);
          axpy(grad, -a.value / (b.value * b.value), b.grad);
          return { value: a.value / b.value, grad };
        }
      }
    }
    // eslint-disable-next-line no-fallthrough
    case 'fn': {
      const a = evalJet(n.x, x);
      const grad = new Map<string, number>();
      let value: number;
      let d: number;
      if (n.fn === 'sqrt') {
        value = Math.sqrt(a.value);
        d = 1 / (2 * value);
      } else if (n.fn === 'sin') {
        value = Math.sin(a.value);
        d = Math.cos(a.value);
      } else {
        value = Math.cos(a.value);
        d = -Math.sin(a.value);
      }
      axpy(grad, d, a.grad);
      return { value, grad };
    }
  }
}

/** A compact infix rendering of a residual node, e.g. `V_bus1 * cos(theta_a - theta_b)`. */
export function rnodeToString(n: RNode): string {
  const fmt = (v: number): string =>
    Number.isInteger(v) ? String(v) : Number(v.toPrecision(4)).toString();
  // Parenthesize a child that could otherwise re-associate under its parent.
  const wrap = (c: RNode): string =>
    c.k === 'bin' || c.k === 'neg' ? `(${rnodeToString(c)})` : rnodeToString(c);
  switch (n.k) {
    case 'const':
      return fmt(n.v);
    case 'var':
      return n.name;
    case 'neg':
      return `-${wrap(n.x)}`;
    case 'fn':
      return `${n.fn}(${rnodeToString(n.x)})`;
    case 'bin':
      return `${wrap(n.l)} ${n.op} ${wrap(n.r)}`;
  }
}

/** Every variable name that appears in a node (its support). */
export function varsOf(n: RNode, into: Set<string> = new Set()): Set<string> {
  switch (n.k) {
    case 'const':
      break;
    case 'var':
      into.add(n.name);
      break;
    case 'neg':
    case 'fn':
      varsOf(n.x, into);
      break;
    case 'bin':
      varsOf(n.l, into);
      varsOf(n.r, into);
      break;
  }
  return into;
}

// ---------------------------------------------------------------------------
// Grounding
// ---------------------------------------------------------------------------

/**
 * Lower a body expression into a residual node, over a shared {@link Grounder}.
 * This is the *only* piece that differs from the linear path: the grounder does
 * the quantifier binding, index/scalar evaluation, data lookup, and variable
 * naming (all reused from `interpret.ts`), and this walks an expression building
 * an {@link RNode} where `linear()` would build a coefficient map — keeping the
 * variable products the linear path throws on.
 */
function makeLower(
  g: Grounder,
  catalog: Catalog,
  varMeta: Map<string, VarMeta>,
): (e: Expr, env: Env) => RNode {
  function lower(e: Expr, env: Env): RNode {
    switch (e.kind) {
      case 'num':
        return konst(e.value);
      case 'ref':
      case 'attr':
        // A data value in a scalar position — folds to a constant.
        return konst(g.numeric(e, env));
      case 'str':
        throw new Error(`a string ("${e.value}") has no numeric value`);
      case 'neg':
        return rneg(lower(e.operand, env));
      case 'binary':
        // `^` isn't a residual-graph op; with a constant non-negative integer
        // exponent it desugars to repeated multiplication (V^2 → V·V), which
        // keeps the graph in the {+,−,×,÷,fn} vocabulary the solver differentiates.
        if (e.op === '^') return lowerPow(e.left, e.right, env);
        return rbin(e.op, lower(e.left, env), lower(e.right, env));
      case 'call': {
        if (e.fn === 'sqrt' || e.fn === 'sin' || e.fn === 'cos') {
          return rfn(e.fn, lower(e.args[0], env));
        }
        // min/max are nonsmooth; only allowed over data (constant-folded).
        return konst(g.numeric(e, env));
      }
      case 'apply': {
        const schema = catalog[e.relation];
        if (schema && schema.kind === 'decision') {
          const name = g.nameForApply(e, env);
          // Record the variable's grouping the first time we see it, so a
          // solution can be tabulated by relation and index like the LP path's.
          if (!varMeta.has(name)) {
            const vals = e.args.flatMap((a) => g.keyValues(a.value, env));
            const indices: Record<string, IndexValue> = {};
            schema.keys.forEach((k, i) => {
              indices[k] = vals[i];
            });
            varMeta.set(name, { group: e.relation, indices });
          }
          return rvar(name);
        }
        // Data relation (e.g. demand): look up the value.
        return konst(g.numeric(e, env));
      }
      case 'sum': {
        let acc: RNode = konst(0);
        for (const senv of g.bindings(e.over, env)) acc = rbin('+', acc, lower(e.body, senv));
        return acc;
      }
      case 'cond':
        return lower(g.truthy(e.when, env) ? e.then : e.otherwise, env);
    }
  }

  /** `base ^ exp` where `exp` is a constant, expanded to repeated multiplication. */
  function lowerPow(base: Expr, exp: Expr, env: Env): RNode {
    const k = g.numeric(exp, env); // the exponent must be a data/constant value
    if (Number.isInteger(k) && k >= 0) {
      if (k === 0) return konst(1);
      const b = lower(base, env);
      let acc = b;
      for (let i = 1; i < k; i++) acc = rbin('*', acc, b);
      return acc;
    }
    // A fractional or negative exponent is only meaningful here when the base
    // folds to a constant; a variable base would leave the residual graph.
    return konst(Math.pow(g.numeric(base, env), k));
  }

  return lower;
}

/**
 * Ground the comparison rules against the data into residuals — one per binding
 * — plus the ordered list of decision variables that appear. `left == right`
 * becomes the residual `left - right`. The quantifier grounding, data lookups,
 * and variable naming are the shared {@link makeGrounder} from `interpret.ts`,
 * verbatim; only the lowering target (an {@link RNode}) is nonlinear-specific.
 */
export function groundResiduals(
  rules: Rule[],
  catalog: Catalog,
  data: Data,
): {
  residuals: GroundedResidual[];
  variables: string[];
  variableMeta: Map<string, VarMeta>;
} {
  const grounder = makeGrounder(data, catalog);
  const { bindings } = grounder;
  const variableMeta = new Map<string, VarMeta>();
  const lower = makeLower(grounder, catalog, variableMeta);
  const residuals: GroundedResidual[] = [];
  const variables: string[] = [];
  const seen = new Set<string>();

  for (const rule of rules) {
    // Only equalities are residuals — an inequality (`<=`/`>=`) has no `= 0`
    // form, so it's skipped rather than mistaken for `left - right = 0`.
    if (rule.body.kind !== 'cmp' || rule.body.op !== '=') continue;
    const { left, right } = rule.body;
    for (const env of bindings(rule.quantifiers, new Map())) {
      const node = rbin('-', lower(left, env), lower(right, env));
      // Column values from the quantifiers, keyed by the relation each ranges
      // over (e.g. { branch: 'l1_2', timestep: 0 }) — the residual's indices,
      // for grouping into a table the way constraints are.
      const indices: Record<string, IndexValue> = {};
      const ids: string[] = [];
      for (const q of rule.quantifiers) {
        const row = env.get(q.var)!.row;
        const keys = catalog[q.relation].keys;
        indices[q.relation] = keys.length === 1 ? row[keys[0]] : keys.map((k) => row[k]).join(':');
        ids.push(...keys.map((k) => formatIndexValue(q.relation, row[k])));
      }
      residuals.push({ name: instanceName(rule.name, ids), group: rule.name, indices, node });
      for (const v of varsOf(node)) {
        if (!seen.has(v)) {
          seen.add(v);
          variables.push(v);
        }
      }
    }
  }

  return { residuals, variables, variableMeta };
}
